import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type ProjectRow } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useLang, type Bilingual } from '../i18n/lang';
import { G } from '../i18n/glossary';

const money = (n: number | null | undefined) => n == null ? '' : `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

// Outcome badge styling.
const OUTCOME: Record<string, { bg: string; c: string; t: string }> = {
  won: { bg: 'var(--teal)', c: '#fff', t: 'Won' },
  lost: { bg: 'var(--danger)', c: '#fff', t: 'Lost' },
  no_bid: { bg: '#e2e2e2', c: '#555', t: 'No-bid' },
};

// Bid/No-Bid scorecard (flywheel #4).
const REC: Record<string, { bg: string; c: string }> = {
  'Pursue': { bg: 'var(--teal)', c: '#fff' },
  'Pursue with caution': { bg: '#f0ad4e', c: '#3a2a00' },
  'Likely pass': { bg: 'var(--danger)', c: '#fff' },
};
const FACTORS = [
  { k: 'fit', label: 'Project fit', hint: 'Our Div 27/28 wheelhouse, size & type' },
  { k: 'margin', label: 'Margin potential', hint: 'Room for a healthy margin' },
  { k: 'relationship', label: 'Relationship / incumbency', hint: 'We know the GC/owner or hold the work' },
  { k: 'competition', label: 'Competitive position', hint: '5 = few bidders, we are favored' },
  { k: 'capacity', label: 'Schedule capacity', hint: '5 = we can comfortably staff it' },
  { k: 'risk', label: 'Low risk', hint: '5 = clear scope, no site/wage surprises' },
];
const WEIGHTS: Record<string, number> = { fit: 0.25, margin: 0.20, relationship: 0.20, competition: 0.15, capacity: 0.10, risk: 0.10 };
const scoreOf = (f: Record<string, number>) => Math.round(Object.keys(WEIGHTS).reduce((s, k) => s + ((f[k] || 3) / 5) * WEIGHTS[k], 0) * 100);
const recOf = (score: number) => score >= 70 ? 'Pursue' : score >= 50 ? 'Pursue with caution' : 'Likely pass';

const PIPELINE = ['Bidding', 'Submitted', 'Won', 'Lost', 'No-bid'] as const;
function stageOf(p: ProjectRow): string {
  if (p.outcome === 'won') return 'Won';
  if (p.outcome === 'lost') return 'Lost';
  if (p.outcome === 'no_bid') return 'No-bid';
  if (p.status === 'submitted' || p.status === 'proposed') return 'Submitted';
  return 'Bidding';
}
function parseScore(s: string | null | undefined): { score: number; recommendation: string } | null {
  if (!s) return null;
  try { const o = JSON.parse(s); return { score: o.score, recommendation: o.recommendation }; } catch { return null; }
}

export function Dashboard() {
  const { user } = useAuth();
  const { t } = useLang();
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState('');
  const [editing, setEditing] = useState<ProjectRow | null>(null);
  const [scoring, setScoring] = useState<ProjectRow | null>(null);
  const [stageFilter, setStageFilter] = useState<string>('');
  const [view, setView] = useState<'mine' | 'sales' | 'all'>(user?.role === 'salesperson' ? 'sales' : 'mine');

  const nameMatch = (a: string | null | undefined) => !!user?.name && (a ?? '').toLowerCase() === user.name.toLowerCase();
  const isMine = (p: ProjectRow) => (!!user && p.created_by === user.id) || nameMatch(p.estimator);
  const isSales = (p: ProjectRow) => nameMatch(p.salesperson);
  const mineCount = projects.filter(isMine).length;
  const salesCount = projects.filter(isSales).length;
  const shown = view === 'mine' ? projects.filter(isMine) : view === 'sales' ? projects.filter(isSales) : projects;

  // Win-rate over the shown set.
  const won = shown.filter((p) => p.outcome === 'won').length;
  const lost = shown.filter((p) => p.outcome === 'lost').length;
  const noBid = shown.filter((p) => p.outcome === 'no_bid').length;
  const open = shown.filter((p) => !p.outcome).length;
  const decided = won + lost;
  const winRate = decided ? Math.round((won / decided) * 100) : null;
  const wonValue = shown.filter((p) => p.outcome === 'won').reduce((s, p) => s + (p.outcome_amount ?? 0), 0);

  // Pipeline stage filter.
  const filtered = stageFilter ? shown.filter((p) => stageOf(p) === stageFilter) : shown;

  function load() {
    api.listProjects()
      .then((r) => setProjects(r.projects))
      .catch((e) => setError(e instanceof Error ? e.message : t({ en: 'Failed to load', fil: 'Hindi na-load' })))
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  async function remove(p: ProjectRow) {
    if (!window.confirm(t({ en: `Delete "${p.name}"? This permanently removes the bid and all its documents, counts, BOM and estimate. This cannot be undone.`, fil: `Burahin ang "${p.name}"? Permanenteng matatanggal ang bid at lahat ng documents, counts, BOM at estimate nito. Hindi na ito maibabalik.` }))) return;
    setDeleting(p.id); setError('');
    try { await api.deleteProject(p.id); setProjects((list) => list.filter((x) => x.id !== p.id)); }
    catch (e) { setError(e instanceof Error ? e.message : t({ en: 'Delete failed', fil: 'Hindi nabura' })); }
    finally { setDeleting(''); }
  }

  function onSaved(updated: ProjectRow) {
    setProjects((list) => list.map((x) => x.id === updated.id ? updated : x));
    setEditing(null);
  }
  function onScored(id: string, bid_score: string) {
    setProjects((list) => list.map((x) => x.id === id ? { ...x, bid_score } : x));
    setScoring(null);
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <div>
          <h1>{t({ en: 'Dashboard', fil: 'Dashboard' })}</h1>
          <p className="muted" style={{ margin: 0 }}>
            {view === 'mine' ? t({ en: 'Bids you estimate', fil: 'Mga bid na ine-estimate mo' }) : view === 'sales' ? t({ en: 'Bids you sell', fil: 'Mga bid na binebenta mo' }) : t({ en: 'All bids (shared — anyone can open and work on these)', fil: 'Lahat ng bid (shared — kahit sino puwedeng magbukas at magtrabaho dito)' })}.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link className="btn btn-gold" to="/fire-alarm-design" title={t({ en: 'California Fire Alarm Design-Build wizard (Budgetary ROM only — not a stamped design)', fil: 'California Fire Alarm Design-Build wizard (Budgetary ROM lang — hindi stamped design)' })}>
            🔥 Design-Build Fire Alarm
          </Link>
          <Link className="btn btn-primary" to="/new">+ {t({ en: 'New Bid', fil: 'Bagong Bid' })}</Link>
        </div>
      </div>

      {/* Win-rate summary (flywheel #1) */}
      {!loading && shown.length > 0 && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <Stat label={t({ en: 'Win rate', fil: 'Win rate' })} value={winRate == null ? '—' : `${winRate}%`} sub={decided ? t({ en: `${won} won / ${lost} lost`, fil: `${won} won / ${lost} lost` }) : t({ en: 'no decided bids yet', fil: 'wala pang na-desisyunan na bid' })} accent="var(--teal)" />
          <Stat label={t({ en: 'Won value', fil: 'Won value' })} value={money(wonValue) || '$0'} sub={t({ en: `${won} awarded`, fil: `${won} na-award` })} accent="var(--gold-dark)" />
          <Stat label={t({ en: 'Open / bidding', fil: 'Open / bidding' })} value={String(open)} sub={t({ en: 'awaiting outcome', fil: 'hinihintay ang resulta' })} />
          <Stat label={t({ en: 'No-bid', fil: 'No-bid' })} value={String(noBid)} sub={t({ en: 'passed on', fil: 'di tinuloy' })} />
        </div>
      )}

      {/* Folder toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button className={`btn btn-sm ${view === 'mine' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setView('mine')}>{t({ en: 'My bids', fil: 'Mga bid ko' })} ({mineCount})</button>
        <button className={`btn btn-sm ${view === 'sales' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setView('sales')}>{t({ en: 'My sales', fil: 'Mga benta ko' })} ({salesCount})</button>
        <button className={`btn btn-sm ${view === 'all' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setView('all')}>{t({ en: 'All bids', fil: 'Lahat ng bid' })} ({projects.length})</button>
      </div>

      {/* Pipeline stage filter (flywheel #4) */}
      {!loading && shown.length > 0 && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <span className="muted" style={{ fontSize: 12, marginRight: 2 }}>{t({ en: 'Pipeline:', fil: 'Pipeline:' })}</span>
          <button className={`btn btn-sm ${stageFilter === '' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setStageFilter('')}>{t({ en: 'All', fil: 'Lahat' })} ({shown.length})</button>
          {PIPELINE.map((s) => {
            const n = shown.filter((p) => stageOf(p) === s).length;
            return <button key={s} className={`btn btn-sm ${stageFilter === s ? 'btn-primary' : 'btn-ghost'}`} disabled={n === 0} onClick={() => setStageFilter(s)}>{s} ({n})</button>;
          })}
        </div>
      )}

      {loading && <div className="muted">{t({ en: 'Loading projects…', fil: 'Naglo-load ng mga project…' })}</div>}
      {error && <div className="error">{error}</div>}

      {!loading && !error && view !== 'all' && shown.length === 0 && projects.length > 0 && (
        <div className="card">
          <h2>{view === 'sales' ? t({ en: 'No bids list you as salesperson yet', fil: 'Wala pang bid na nakalista sa iyo bilang salesperson' }) : t({ en: 'No bids assigned to you yet', fil: 'Wala pang bid na naka-assign sa iyo' })}</h2>
          <p className="muted">{t({ en: 'Switch to', fil: 'Lumipat sa' })} <strong>{t({ en: 'All bids', fil: 'Lahat ng bid' })}</strong> {t({ en: "to see and work on the whole team's bids.", fil: 'para makita at matrabaho ang mga bid ng buong team.' })}</p>
          <button className="btn btn-ghost" onClick={() => setView('all')}>{t({ en: 'View all bids', fil: 'Tingnan lahat ng bid' })}</button>
        </div>
      )}

      {!loading && !error && projects.length === 0 && (
        <div className="card">
          <h2>{t({ en: 'No bids yet', fil: 'Wala pang bid' })}</h2>
          <p className="muted">{t({ en: 'Create your first bid to begin. The AI reads the documents; your estimators do all counting.', fil: 'Gumawa ng unang bid mo para magsimula. Binabasa ng AI ang mga documents; ang mga estimator mo ang gagawa ng lahat ng counting.' })}</p>
          <Link className="btn btn-gold" to="/new">{t({ en: 'Create a bid', fil: 'Gumawa ng bid' })}</Link>
        </div>
      )}

      {shown.length > 0 && (
        <div className="card" style={{ padding: 0 }}>
          <table>
            <thead>
              <tr>
                <th>{t({ en: 'Project', fil: 'Project' })}</th><th>📁 {t({ en: 'Folder', fil: 'Folder' })}</th><th>{t({ en: 'Customer', fil: 'Customer' })}</th><th>{t({ en: 'County / State', fil: 'County / State' })}</th>
                <th>{t({ en: 'Bid date', fil: 'Bid date' })}</th><th title={t({ en: 'Bid/No-Bid recommendation', fil: 'Bid/No-Bid recommendation' })}>{t({ en: 'Pursue?', fil: 'Ituloy?' })}</th><th>{t(G.status)}</th><th>{t({ en: 'Outcome', fil: 'Resulta' })}</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id}>
                  <td>
                    <Link to={p.is_fa ? `/fire-alarm-design?project=${p.id}` : `/projects/${p.id}`}>{p.name}</Link>
                    {!!p.is_fa && <span title={t({ en: 'Fire Alarm Design-Build estimate', fil: 'Fire Alarm Design-Build estimate' })} style={{ marginLeft: 6, fontSize: 11, background: 'var(--gold-light)', color: 'var(--gold-dark)', padding: '1px 6px', borderRadius: 8, fontWeight: 700 }}>🔥 FA</span>}
                  </td>
                  <td title={t({ en: 'The folder this bid was created in — it stays here; anyone can open & work on it', fil: 'Ang folder kung saan ginawa ang bid na ito — dito ito mananatili; kahit sino puwedeng magbukas at magtrabaho dito' })}>
                    {p.creator_name ?? '—'}{!!user && p.created_by === user.id && <span className="muted"> ({t({ en: 'you', fil: 'ikaw' })})</span>}
                  </td>
                  <td>{p.customer_name ?? '—'}</td>
                  <td>{[p.county, p.state].filter(Boolean).join(', ') || '—'}</td>
                  <td>{p.bid_date ?? '—'}</td>
                  <td>
                    {(() => {
                      const bs = parseScore(p.bid_score);
                      return bs
                        ? <button className="link-btn" title={t({ en: `Score ${bs.score}/100 — click to revise`, fil: `Score ${bs.score}/100 — i-click para baguhin` })} onClick={() => setScoring(p)}>
                            <span style={{ background: REC[bs.recommendation].bg, color: REC[bs.recommendation].c, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700 }}>{bs.recommendation}</span>
                            <span className="muted" style={{ fontSize: 11 }}> {bs.score}</span>
                          </button>
                        : <button className="btn btn-ghost btn-sm" onClick={() => setScoring(p)}>+ {t({ en: 'Score', fil: 'Score' })}</button>;
                    })()}
                  </td>
                  <td><span className={`pill pill-${p.status}`}>{p.status}</span></td>
                  <td>
                    {p.outcome
                      ? <button className="link-btn" title={p.outcome_reason || t({ en: 'Edit outcome', fil: 'I-edit ang resulta' })} onClick={() => setEditing(p)}>
                          <span style={{ background: OUTCOME[p.outcome].bg, color: OUTCOME[p.outcome].c, padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>{OUTCOME[p.outcome].t}</span>
                          {p.outcome === 'won' && p.outcome_amount ? <span className="muted" style={{ fontSize: 11 }}> {money(p.outcome_amount)}</span> : null}
                        </button>
                      : <button className="btn btn-ghost btn-sm" onClick={() => setEditing(p)}>+ {t({ en: 'Record', fil: 'I-record' })}</button>}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="btn btn-ghost btn-sm" disabled={deleting === p.id} title={t({ en: 'Delete bid', fil: 'Burahin ang bid' })} onClick={() => remove(p)}>
                      {deleting === p.id ? '…' : '🗑'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <OutcomeModal project={editing} onClose={() => setEditing(null)} onSaved={onSaved} setError={setError} />}
      {scoring && <BidScoreModal project={scoring} onClose={() => setScoring(null)} onScored={onScored} setError={setError} />}
    </div>
  );
}

// Bilingual labels/hints for scorecard factors (keyed by FACTORS[].k).
const FACTOR_I18N: Record<string, { label: Bilingual; hint: Bilingual }> = {
  fit: { label: { en: 'Project fit', fil: 'Bagay ba ang project' }, hint: { en: 'Our Div 27/28 wheelhouse, size & type', fil: 'Ang Div 27/28 wheelhouse natin, laki at type' } },
  margin: { label: { en: 'Margin potential', fil: 'Margin potential' }, hint: { en: 'Room for a healthy margin', fil: 'May puwang ba para sa magandang margin' } },
  relationship: { label: { en: 'Relationship / incumbency', fil: 'Relasyon / incumbency' }, hint: { en: 'We know the GC/owner or hold the work', fil: 'Kilala natin ang GC/owner o hawak natin ang trabaho' } },
  competition: { label: { en: 'Competitive position', fil: 'Posisyon sa kompetisyon' }, hint: { en: '5 = few bidders, we are favored', fil: '5 = kaunti ang bidders, paborito tayo' } },
  capacity: { label: { en: 'Schedule capacity', fil: 'Kapasidad sa schedule' }, hint: { en: '5 = we can comfortably staff it', fil: '5 = kaya nating i-staff nang komportable' } },
  risk: { label: { en: 'Low risk', fil: 'Mababang risk' }, hint: { en: '5 = clear scope, no site/wage surprises', fil: '5 = malinaw ang scope, walang sorpresa sa site/wage' } },
};

function BidScoreModal({ project, onClose, onScored, setError }: { project: ProjectRow; onClose: () => void; onScored: (id: string, bid_score: string) => void; setError: (s: string) => void }) {
  const { t } = useLang();
  const init = (() => { try { return project.bid_score ? JSON.parse(project.bid_score) : null; } catch { return null; } })();
  const [f, setF] = useState<Record<string, number>>(() => {
    const base: Record<string, number> = {};
    for (const x of FACTORS) base[x.k] = init?.factors?.[x.k] ?? 3;
    return base;
  });
  const [notes, setNotes] = useState<string>(init?.notes ?? '');
  const [busy, setBusy] = useState(false);
  const score = scoreOf(f);
  const rec = recOf(score);

  async function save() {
    setBusy(true);
    try {
      const res = await api.setBidScore(project.id, { factors: f, notes });
      onScored(project.id, res.bid_score);
    } catch (e) { setError(e instanceof Error ? e.message : t({ en: 'Save failed', fil: 'Hindi na-save' })); }
    finally { setBusy(false); }
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ width: 540, maxWidth: '94vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <h2 style={{ marginTop: 0 }}>{t({ en: 'Bid / No-Bid scorecard', fil: 'Bid / No-Bid scorecard' })}</h2>
        <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>{project.name} — {t({ en: 'rate each 1 (poor) to 5 (excellent). Score weights fit, margin & relationship most.', fil: 'i-rate ang bawat isa mula 1 (mahina) hanggang 5 (napakaganda). Pinakamabigat sa score ang fit, margin at relasyon.' })}</p>
        {FACTORS.map((x) => (
          <div key={x.k} style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <label style={{ fontSize: 13, fontWeight: 600 }}>{t(FACTOR_I18N[x.k]?.label ?? { en: x.label, fil: x.label })}</label>
              <span className="muted" style={{ fontSize: 11 }}>{t(FACTOR_I18N[x.k]?.hint ?? { en: x.hint, fil: x.hint })}</span>
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" className={`btn btn-sm ${f[x.k] === n ? 'btn-primary' : 'btn-ghost'}`} style={{ minWidth: 38 }} onClick={() => setF((p) => ({ ...p, [x.k]: n }))}>{n}</button>
              ))}
            </div>
          </div>
        ))}
        <Field label={t({ en: 'Notes (optional)', fil: 'Mga tala (opsyonal)' })}><input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t({ en: 'bid-day strategy, who to call, deal-breakers…', fil: 'strategy sa bid-day, sino tatawagan, mga deal-breaker…' })} /></Field>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, padding: '10px 14px', borderRadius: 8, background: 'var(--gray-50, #f7f7f7)' }}>
          <div style={{ fontSize: 30, fontWeight: 800, color: REC[rec].bg }}>{score}</div>
          <div>
            <span style={{ background: REC[rec].bg, color: REC[rec].c, padding: '2px 10px', borderRadius: 10, fontSize: 13, fontWeight: 700 }}>{rec}</span>
            <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{t({ en: 'Weighted winnability score (0–100)', fil: 'Weighted winnability score (0–100)' })}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button className="btn btn-ghost" onClick={onClose}>{t(G.cancel)}</button>
          <button className="btn btn-primary" disabled={busy} onClick={save}>{busy ? t(G.saving) : t({ en: 'Save scorecard', fil: 'I-save ang scorecard' })}</button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <div style={{ flex: 1, minWidth: 130, border: '1px solid var(--gray-100)', borderRadius: 8, padding: '10px 14px' }}>
      <div className="muted" style={{ fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: accent || 'var(--ink, #111)' }}>{value}</div>
      {sub && <div className="muted" style={{ fontSize: 11 }}>{sub}</div>}
    </div>
  );
}

function OutcomeModal({ project, onClose, onSaved, setError }: { project: ProjectRow; onClose: () => void; onSaved: (p: ProjectRow) => void; setError: (s: string) => void }) {
  const { t } = useLang();
  const [outcome, setOutcome] = useState<string>(project.outcome ?? '');
  const [amount, setAmount] = useState<string>(project.outcome_amount ? String(project.outcome_amount) : '');
  const [competitor, setCompetitor] = useState<string>(project.competitor ?? '');
  const [reason, setReason] = useState<string>(project.outcome_reason ?? '');
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      const out = outcome || null;
      await api.setOutcome(project.id, { outcome: out, amount: amount ? Number(amount) : null, competitor: competitor || null, reason: reason || null });
      onSaved({
        ...project, outcome: (out as ProjectRow['outcome']) ?? null,
        outcome_amount: out && amount ? Number(amount) : null,
        competitor: out ? (competitor || null) : null,
        outcome_reason: out ? (reason || null) : null,
      });
    } catch (e) { setError(e instanceof Error ? e.message : t({ en: 'Save failed', fil: 'Hindi na-save' })); }
    finally { setBusy(false); }
  }

  const opt = (v: string, label: string) => (
    <button type="button" className={`btn btn-sm ${outcome === v ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setOutcome(v)}>{label}</button>
  );

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ width: 460, maxWidth: '92vw' }}>
        <h2 style={{ marginTop: 0 }}>{t({ en: 'Record bid outcome', fil: 'I-record ang resulta ng bid' })}</h2>
        <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>{project.name}</p>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          {opt('won', t({ en: 'Won', fil: 'Won' }))}{opt('lost', t({ en: 'Lost', fil: 'Lost' }))}{opt('no_bid', t({ en: 'No-bid', fil: 'No-bid' }))}{opt('', t({ en: 'Open (clear)', fil: 'Open (i-clear)' }))}
        </div>
        {outcome && outcome !== 'no_bid' && (
          <>
            <Field label={outcome === 'won' ? t({ en: 'Our winning bid $ (optional)', fil: 'Ang panalo nating bid $ (opsyonal)' }) : t({ en: 'Winning bid $ — who beat us (optional)', fil: 'Panalong bid $ — sino ang tumalo sa atin (opsyonal)' })}>
              <input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={t({ en: 'e.g. 154000', fil: 'hal. 154000' })} />
            </Field>
            <Field label={t({ en: 'Competitor (optional)', fil: 'Competitor (opsyonal)' })}><input value={competitor} onChange={(e) => setCompetitor(e.target.value)} placeholder={t({ en: 'who we beat / who beat us', fil: 'sino tinalo natin / sino tumalo sa atin' })} /></Field>
          </>
        )}
        {outcome && (
          <Field label={t({ en: 'Reason / notes (optional)', fil: 'Dahilan / mga tala (opsyonal)' })}><input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={outcome === 'won' ? t({ en: 'why we won', fil: 'bakit tayo nanalo' }) : outcome === 'lost' ? t({ en: 'price? scope? schedule?', fil: 'presyo? scope? schedule?' }) : t({ en: 'why we passed', fil: 'bakit di natin tinuloy' })} /></Field>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button className="btn btn-ghost" onClick={onClose}>{t(G.cancel)}</button>
          <button className="btn btn-primary" disabled={busy} onClick={save}>{busy ? t(G.saving) : t({ en: 'Save outcome', fil: 'I-save ang resulta' })}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 3 }}>{label}</label>
      {children}
    </div>
  );
}
